import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import type CK from '~/types/contentking';
import GraphQL from '~/types/graphql';

import {
	type AbstractCheckboxFieldCheckedState,
} from '~/components/patterns/forms/fields/AbstractCheckboxField';
import ButtonsLayout, {
	ButtonsLayoutType,
} from '~/components/patterns/buttons/ButtonsLayout';
import CancelButton from '~/components/app/CancelButton';
import DisplayPart from '~/components/atoms/forms/basis/DisplayPart';
import EditableFormWrapper from '~/components/atoms/forms/basis/EditableFormWrapper';
import EditablePart from '~/components/atoms/forms/basis/EditablePart';
import Form from '~/components/atoms/forms/basis/Form';
import FormRow from '~/components/atoms/forms/basis/FormRow';
import FormRows from '~/components/atoms/forms/basis/FormRows';
import Measurer from '~/utilities/Measurer';
import MultiselectListTable from '~/components/atoms/forms/components/MultiselectListTable';
import SaveSubmitButton from '~/components/app/SaveSubmitButton';
import StaticText from '~/components/atoms/forms/components/StaticText';

import {
	useUpdateDefaultSendingOfAlertsAndReportsToUserMutation,
} from './WebsiteReportsDefaultAlertsForm.gql';

import useAllWebsites from '~/hooks/useAllWebsites';
import useAllWebsitesDefaultAlertsAndReports from '~/hooks/useAllWebsitesDefaultAlertsAndReports';
import useIsAllowedWithUserWebsiteAccessOracle from '~/hooks/useIsAllowedWithUserWebsiteAccessOracle';

import {
	combinePermissionChecks,
} from '~/model/permissions';



const messages = defineMessages({
	assignedWebsitesCount: {
		id: 'ui.settings.emailNotifications.website.view',
	},
	labelAlerts: {
		id: 'ui.events.title',
	},
	labelReports: {
		id: 'ui.settings.emailNotifications.reports',
	},
	labelWebsite: {
		id: 'ui.settings.emailNotifications.website',
	},
	title: {
		id: 'ui.settings.defaultEmailNotifications.title',
	},
});



type Props = {
	userAccountMemberships: ReadonlyArray<{
		account: {
			id: CK.AccountId,
		},
		websites: ReadonlyArray<{
			id: CK.WebsiteId,
		}>,
	}> | null,
	userId: CK.UserId,
	accountId: CK.AccountId,
};

const WebsiteReportsDefaultAlertsForm: React.FC<Props> = (props) => {
	const {
		userAccountMemberships,
		userId,
		accountId,
	} = props;

	const allWebsites = useAllWebsites();

	const isAllowedWithUserWebsiteAccessOracle = useIsAllowedWithUserWebsiteAccessOracle();

	const allWebsitesDefaultAlertsAndReports = useAllWebsitesDefaultAlertsAndReports(userId, accountId);
	const user = allWebsitesDefaultAlertsAndReports.getUser();
	const account = allWebsitesDefaultAlertsAndReports.getAccount();

	const [updateDefaultSendingOfAlertsAndReportsToUser] = useUpdateDefaultSendingOfAlertsAndReportsToUserMutation();

	const handleSubmit = React.useCallback(
		async (data) => {
			if (user === null) {
				return;
			}

			const newEmailSettings = allWebsites.listByAccount(accountId).map((website) => {
				const { id: websiteId } = website;
				return {
					alerts: data.emailSettings.alerts[websiteId],
					id: websiteId,
					reports: data.emailSettings.weeklyReports[websiteId],
				};
			});

			await updateDefaultSendingOfAlertsAndReportsToUser({
				variables: {
					userId,
					websites: newEmailSettings,
					accountId,
				},
			});
		},
		[
			accountId,
			allWebsites,
			updateDefaultSendingOfAlertsAndReportsToUser,
			user,
			userId,
		],
	);

	const calculatedData = React.useMemo(
		() => {
			if (
				allWebsites.isLoaded === false
				|| allWebsitesDefaultAlertsAndReports.isLoaded === false
				|| userAccountMemberships === null
			) {
				return null;
			}

			const websitePermissionChecks: Array<ReturnType<typeof isAllowedWithUserWebsiteAccessOracle>> = [];

			const websitesList: Array<{
				disabled: boolean,
				label: string,
				name: string,
			}> = [];

			const emailSettings: {
				alerts: Record<CK.WebsiteId, boolean | AbstractCheckboxFieldCheckedState.Indeterminate>,
				weeklyReports: Record<CK.WebsiteId, boolean>,
			} = {
				alerts: {},
				weeklyReports: {},
			};

			let alertsCount = 0;
			let weeklyReportsCount = 0;

			allWebsites.listByAccount(account.id).forEach((website) => {
				const userHasAccess = userAccountMemberships.some(
					(membership) => membership.websites.some((userWebsite) => userWebsite.id === website.id),
				);

				if (userHasAccess === false) {
					return;
				}

				const permissionCheck = isAllowedWithUserWebsiteAccessOracle({
					action: GraphQL.ActionWithUserWebsiteAccess.ManageEmailPreferences,
					from: 'user',
					uniqueUserId: userId,
					websiteId: website.id,
				});

				websitePermissionChecks.push(permissionCheck);

				websitesList.push({
					disabled: permissionCheck.no,
					label: website.displayName,
					name: website.id,
				});

				const websiteEmailSettings = allWebsitesDefaultAlertsAndReports.listDefaults().find(
					(websiteEmailSettings) => websiteEmailSettings.websiteId === website.id,
				);

				if (websiteEmailSettings === undefined) {
					return;
				}

				emailSettings.weeklyReports[website.id] = websiteEmailSettings.reports;

				emailSettings.alerts[website.id] = websiteEmailSettings.alerts;

				if (websiteEmailSettings.reports) {
					weeklyReportsCount++;
				}

				if (websiteEmailSettings.alerts) {
					alertsCount++;
				}
			});

			return {
				alertsCount,
				emailSettings,
				websitePermissionChecks,
				websitesList,
				weeklyReportsCount,
			};
		},
		[
			account,
			allWebsitesDefaultAlertsAndReports,
			allWebsites,
			isAllowedWithUserWebsiteAccessOracle,
			userAccountMemberships,
			userId,
		],
	);

	if (calculatedData === null) {
		return null;
	}

	return (
		<>
			<EditableFormWrapper
				isAllowed={combinePermissionChecks(calculatedData.websitePermissionChecks)}
				key={userId}
				title={(
					<FormattedMessage {...messages.title} />
				)}
			>
				<DisplayPart>
					<FormRows>
						<FormRow
							label={(
								<FormattedMessage {...messages.labelReports} />
							)}
						>
							<StaticText>
								<FormattedMessage
									{...messages.assignedWebsitesCount}
									values={{
										websitesCount: calculatedData.weeklyReportsCount,
									}}
								/>
							</StaticText>
						</FormRow>

						<FormRow
							label={(
								<FormattedMessage {...messages.labelAlerts} />
							)}
						>
							<StaticText>
								<FormattedMessage
									{...messages.assignedWebsitesCount}
									values={{
										websitesCount: calculatedData.alertsCount,
									}}
								/>
							</StaticText>
						</FormRow>
					</FormRows>
				</DisplayPart>

				<EditablePart>
					<Form
						defaultFocus="emailSettings"
						defaultValues={{
							emailSettings: calculatedData.emailSettings,
						}}
						onSuccess={handleSubmit}
					>
						<FormRows>
							<FormRow
								fullwidth={true}
								htmlFor="emailSettings"
							>
								<Measurer>
									{({ containerWidth }) => (
										<MultiselectListTable
											columns={[
												{
													name: 'weeklyReports',
													label: (
														<FormattedMessage {...messages.labelReports} />
													),
												},
												{
													name: 'alerts',
													label: (
														<FormattedMessage {...messages.labelAlerts} />
													),
												},
											]}
											label={(
												<FormattedMessage {...messages.labelWebsite} />
											)}
											name="emailSettings"
											rows={calculatedData.websitesList}
											tableWidth={containerWidth}
										/>
									)}
								</Measurer>
							</FormRow>
						</FormRows>

						<ButtonsLayout layout={ButtonsLayoutType.FormRowWithoutStatus}>
							<CancelButton />
							<SaveSubmitButton />
						</ButtonsLayout>
					</Form>
				</EditablePart>
			</EditableFormWrapper>
		</>
	);
};



export default WebsiteReportsDefaultAlertsForm;
